import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaTelegram, FaUsers, FaTimes } from 'react-icons/fa';
import { Check } from 'lucide-react';
import BottomNavigation from './BottomNavigation';
import { useAuth } from '../security/security/AuthContext';
import axios from 'axios';
import WhatsAppFloat from './auth/WhatsAppFloat';
import { GiReceiveMoney } from "react-icons/gi";

const Toast = ({ message, isVisible, onHide }) => {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(onHide, 4000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onHide]);

  if (!isVisible) return null;

  return (
    <div className="team-page-toast">
      <div className="team-page-toast-icon">
        <Check size={16} />
      </div>
      <span className="team-page-toast-message">{message}</span>
    </div>
  );
};

const LevelModal = ({ level, stats, referrals = [], loading, onClose, initiateWhatsAppChat }) => {
  if (!level) return null;

  return (
    <div className="team-page-modal-overlay" onClick={onClose}>
      <div className="team-page-modal" onClick={e => e.stopPropagation()}>
        <div className="team-page-modal-header">
          <h3>Level {level} Referrals</h3>
          <button className="team-page-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        {loading ? (
          <div className="team-page-loading-spinner">Loading...</div>
        ) : Array.isArray(referrals) && referrals.length > 0 ? (
          <div className="team-page-referrals-grid">
            {referrals.map((referral) => (
              <div key={referral.id || Math.random()} className="team-page-referral-card">
                <div className="team-page-referral-header">
                  <h4>{referral.fullName}</h4>
                  <button 
                    className="team-page-whatsapp-button" 
                    onClick={() => initiateWhatsAppChat(referral.mobile)}
                  >
                    <FaWhatsapp size={20} />
                  </button>
                </div>
                <div className="team-page-referral-details">
                  <p>Joined: {new Date(referral.joinDate).toLocaleDateString()}</p>
                  <p>Total Deposits: KES {referral.totalDeposits?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="team-page-no-data">No referrals found for this level</p>
        )}
      </div>
    </div>
  );
};

const Team = () => {
  const { userDetails, token } = useAuth();
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({
    totalReferrals: 0,
    totalEarnings: 0,
    levelStats: {
      level1: { count: 0, earnings: 0 },
      level2: { count: 0, earnings: 0 },
      level3: { count: 0, earnings: 0 }
    }
  });
  const [inviteLink, setInviteLink] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchReferrals = async (level) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/referrals/level/${level}`,
        { headers: { 'Authorization': token } }
      );
      setReferrals(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching referrals:', error);
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/referrals/stats`,
        { headers: { 'Authorization': token } }
      );
      setStats(response.data || {
        totalReferrals: 0,
        totalEarnings: 0,
        levelStats: {
          level1: { count: 0, earnings: 0 },
          level2: { count: 0, earnings: 0 },
          level3: { count: 0, earnings: 0 }
        }
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    if (userDetails?.invitationCode) {
      setInviteLink(`${window.location.origin}/signup?invite_code=${userDetails.invitationCode}`);
    }
  }, [userDetails?.invitationCode]);

  const handleLevelClick = (level) => {
    setSelectedLevel(level);
    fetchReferrals(level);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteLink);
    setShowToast(true);
  };

  const shareViaWhatsApp = () => {
    const message = `Join us using my invitation link: ${inviteLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const shareViaTelegram = () => {
    const message = `Join us using my invitation link: ${inviteLink}`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(message)}`, '_blank');
  };

  const initiateWhatsAppChat = (mobile) => {
    if (!mobile) return;
    const formattedMobile = mobile.startsWith('+') ? mobile.substring(1) : mobile;
    window.open(`https://wa.me/${formattedMobile}`, '_blank');
  };

  return (
    <div className="team-page-wrapper">
      <div className="team-page-container">
        <header className="team-page-header">
          <h1 className="team-page-title">My Team Dashboard</h1>
          
          <div className="team-page-overview-card">
            <div className="team-page-overview-stat">
              <div className="team-page-stat-icon">
                <FaUsers />
              </div>
              <div className="team-page-stat-content">
                <h2>{userDetails?.totalReferrals || 0}</h2>
                <p>Total Referrals</p>
              </div>
            </div>
            
            <div className="team-page-overview-divider" />
            
            <div className="team-page-overview-stat">
              <div className="team-page-stat-icon">
                <GiReceiveMoney />
              </div>
              <div className="team-page-stat-content">
                <h2>KES {stats?.totalEarnings?.toFixed(2) || '0.00'}</h2>
                <p>Total Earnings</p>
              </div>
            </div>
          </div>
        </header>

        <div className="team-page-levels-container">
          <div className="team-page-levels-scroll">
            {[1, 2, 3].map((level) => (
              <div
                key={level}
                className="team-page-level-card"
                onClick={() => handleLevelClick(level)}
              >
                <div className="team-page-level-content">
                  <FaUsers className="team-page-level-icon" />
                  <h3>Level {level}</h3>
                  <div className="team-page-level-stats">
                    <p>{stats?.levelStats?.[`level${level}`]?.count || 0} referrals</p>
                    <p>KES {stats?.levelStats?.[`level${level}`]?.earnings?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <LevelModal
          level={selectedLevel}
          stats={stats}
          referrals={referrals}
          loading={loading}
          onClose={() => setSelectedLevel(null)}
          initiateWhatsAppChat={initiateWhatsAppChat}
        />

        <section className="team-page-invite-section">
          <div className="team-page-invite-container" onClick={copyToClipboard}>
            <h3>Your Invitation Link</h3>
            <div className="team-page-invite-link">
              <p>{inviteLink}</p>
            </div>
          </div>

          <div className="team-page-share-section">
            <h3 className="team-page-share-title">Share via</h3>
            <div className="team-page-share-buttons">
              <button className="team-page-share-button team-page-whatsapp" onClick={shareViaWhatsApp}>
                <FaWhatsapp /> WhatsApp
              </button>
              <button className="team-page-share-button team-page-telegram" onClick={shareViaTelegram}>
                <FaTelegram /> Telegram
              </button>
            </div>
          </div>
        </section>

        <Toast 
          message="Link copied to clipboard!" 
          isVisible={showToast} 
          onHide={() => setShowToast(false)} 
        />
      </div>

      <BottomNavigation />
      <WhatsAppFloat />
    </div>
  );
};

export default Team;
